import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import MarkdownRenderer from '../components/MarkdownRenderer';
import IssueComments from '../components/IssueComments';

interface PortalIssue {
  id: number;
  type: 'bug' | 'feature';
  title: string;
  description: string | null;
  status: string;
  created_at: string;
  updated_at: string;
  created_by_name: string | null;
  created_by_email: string | null;
}

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'done', label: 'Done' },
  { value: 'wontfix', label: "Won't fix" },
];

const MyFeedback: React.FC = () => {
  const [issues, setIssues] = useState<PortalIssue[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState<'all' | 'bug' | 'feature'>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const selected = issues.find((i) => i.id === selectedId);

  const fetchIssues = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType !== 'all') params.set('type', filterType);
      if (filterStatus !== 'all') params.set('status', filterStatus);
      const res = await api.get<PortalIssue[]>(`/portal-issues/mine?${params.toString()}`);
      setIssues(res.data);
      if (selectedId && !res.data.find((i) => i.id === selectedId)) {
        setSelectedId(null);
        setEditingId(null);
      }
    } catch (err) {
      console.error('Failed to fetch my issues:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIssues();
  }, [filterType, filterStatus]);

  const startEditing = (issue: PortalIssue) => {
    setEditingId(issue.id);
    setEditTitle(issue.title);
    setEditDescription(issue.description || '');
    setEditStatus(issue.status);
    setSaveError('');
  };

  const cancelEditing = () => {
    setEditingId(null);
    setSaveError('');
  };

  const saveEdit = async () => {
    if (editingId == null) return;
    setSaveError('');
    setSaving(true);
    try {
      const res = await api.patch(`/portal-issues/${editingId}`, {
        title: editTitle.trim(),
        description: editDescription.trim() || null,
        status: editStatus,
      });
      const updated = res.data as PortalIssue;
      setIssues((prev) => prev.map((i) => (i.id === editingId ? { ...i, ...updated } : i)));
      setEditingId(null);
      if (selectedId === editingId) setSelectedId(null);
    } catch (err: any) {
      setSaveError(err.response?.data?.error || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const updateStatusOnly = async (id: number, status: string) => {
    try {
      const res = await api.patch(`/portal-issues/${id}`, { status });
      const updated = res.data as PortalIssue;
      setIssues((prev) => prev.map((i) => (i.id === id ? { ...i, ...updated } : i)));
      if (selectedId === id) setSelectedId(null);
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const formatDate = (s: string) => {
    const d = new Date(s);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) + ' ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">My feedback</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Issues you’ve submitted. You can edit and track their status here.
          </p>
        </div>
        <Link
          to="/feedback"
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
        >
          + New request
        </Link>
      </div>

      <div className="flex flex-wrap gap-4 items-center">
        <span className="text-sm text-gray-600 dark:text-gray-400">Filters:</span>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value as 'all' | 'bug' | 'feature')}
          className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:ring-indigo-500 focus:border-indigo-500"
        >
          <option value="all">All types</option>
          <option value="bug">Bugs</option>
          <option value="feature">Features</option>
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:ring-indigo-500 focus:border-indigo-500"
        >
          <option value="all">All statuses</option>
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      <div className="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">Loading…</div>
        ) : issues.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            You haven’t submitted any feedback yet.{' '}
            <Link to="/feedback" className="text-indigo-600 dark:text-indigo-400 hover:underline">
              Request a feature or report a bug
            </Link>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {issues.map((issue) => (
              <li key={issue.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <div
                  className="px-4 py-3 flex items-start gap-3 cursor-pointer"
                  onClick={() => editingId !== issue.id && setSelectedId(selectedId === issue.id ? null : issue.id)}
                >
                  <span className="flex-shrink-0 text-gray-400 dark:text-gray-500 font-mono text-sm">#{issue.id}</span>
                  <span
                    className={`flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      issue.type === 'bug'
                        ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                        : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                    }`}
                  >
                    {issue.type === 'bug' ? 'Bug' : 'Feature'}
                  </span>
                  <span
                    className={`flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      issue.status === 'open'
                        ? 'bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-200'
                        : issue.status === 'in_progress'
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                        : issue.status === 'done'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-600 dark:text-gray-400'
                    }`}
                  >
                    {STATUS_OPTIONS.find((o) => o.value === issue.status)?.label || issue.status}
                  </span>
                  <span className="flex-1 min-w-0 font-medium text-gray-900 dark:text-gray-100 truncate">
                    {issue.title}
                  </span>
                  <span className="flex-shrink-0 text-sm text-gray-500 dark:text-gray-400" title={issue.created_at}>
                    {formatDate(issue.created_at)}
                  </span>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); startEditing(issue); }}
                    className="flex-shrink-0 text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
                  >
                    Edit
                  </button>
                </div>
                {selectedId === issue.id && editingId !== issue.id && (
                  <div className="px-4 pb-4 pt-0 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 w-full">
                    <div className="pt-3 space-y-3 w-full min-w-0">
                      {issue.description ? (
                        <div className="prose prose-sm dark:prose-invert max-w-none">
                          <MarkdownRenderer content={issue.description || ''} />
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 dark:text-gray-400">No description.</p>
                      )}
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Status:</span>
                        <select
                          value={issue.status}
                          onChange={(e) => updateStatusOnly(issue.id, e.target.value)}
                          className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                        >
                          {STATUS_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          Updated {formatDate(issue.updated_at)}
                        </span>
                        <button
                          type="button"
                          onClick={() => startEditing(issue)}
                          className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
                        >
                          Edit title & description
                        </button>
                      </div>
                      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                        <IssueComments issueId={issue.id} />
                      </div>
                    </div>
                  </div>
                )}
                {editingId === issue.id && (
                  <div className="px-4 pb-4 pt-3 border-t border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title</label>
                      <input
                        type="text"
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                      <textarea
                        rows={4}
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder="Optional details, steps to reproduce, or desired behavior..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                      <select
                        value={editStatus}
                        onChange={(e) => setEditStatus(e.target.value)}
                        className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                      >
                        {STATUS_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </div>
                    {saveError && <p className="text-sm text-red-600 dark:text-red-400">{saveError}</p>}
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={saveEdit}
                        disabled={saving || !editTitle.trim()}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                      >
                        {saving ? 'Saving…' : 'Save'}
                      </button>
                      <button
                        type="button"
                        onClick={cancelEditing}
                        disabled={saving}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default MyFeedback;
